// frontend/src/hooks/useChat.js
import { useState, useEffect } from 'react';
import { sendQuestion, sendFeedback } from '../services/api';

export const useChat = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Load messages from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('chat_messages');
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.error('Error parsing saved messages', e);
        localStorage.removeItem('chat_messages');
      }
    }
  }, []);
  
  // Save messages to localStorage when they change
  useEffect(() => {
    localStorage.setItem('chat_messages', JSON.stringify(messages));
  }, [messages]);
  
  const addMessage = (message) => {
    setMessages(prevMessages => [...prevMessages, message]);
  };
  
  const handleSendMessage = async (text, tonePreference = 'casual') => {
    // Add user message immediately
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    addMessage(userMessage);
    
    // Set loading state
    setLoading(true);
    
    try {
      // Call API to get response with tone preference
      const response = await sendQuestion(text, tonePreference);
      
      // Add assistant response
      const assistantMessage = {
        id: response.message_id,
        role: 'assistant',
        text: response.answer,
        citations: response.citations || [],
        session_id: response.session_id,
        conversation_examples: response.conversation_examples,
        conversation_difficulty: response.conversation_difficulty,
        crisis_data: response.crisis_data,
        response_tone: response.response_tone
      };
      addMessage(assistantMessage);
    } catch (error) {
      // Add error message
      addMessage({
        id: Date.now().toString(),
        role: 'assistant',
        text: 'Sorry, I had trouble processing your question. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleProvideFeedback = async (messageId, isHelpful, comment = null) => {
    // Update message with feedback status
    setMessages(prevMessages => 
      prevMessages.map(msg => 
        msg.id === messageId 
          ? { ...msg, feedback: { isHelpful, timestamp: new Date() } } 
          : msg
      )
    );
    
    // Send feedback to API
    await sendFeedback(messageId, isHelpful, comment);
  };
  
  return {
    messages,
    loading,
    sendMessage: handleSendMessage,
    provideFeedback: handleProvideFeedback
  };
};